<template>
  <Dialog
    :visible="modelValue"
    @update:visible="$emit('update:modelValue', $event)"
    modal
    :style="{ width: '46rem' }"
    :breakpoints="{ '960px': '95vw' }"
    :header="year ? `إعدادات اشتراك — ${year.name}` : 'إعدادات الاشتراك'"
  >
    <div v-if="year" class="row g-3">
      <div class="col-12">
        <label class="form-label">الوصف</label>
        <textarea class="form-control" rows="2" v-model="form.description"></textarea>
      </div>
      <div class="col-md-6">
        <label class="form-label">الصورة</label>
        <input type="file" class="form-control" accept="image/*" @change="onImage" />
        <img v-if="preview" :src="preview" class="mt-2 rounded" style="max-height:70px" alt="" />
      </div>
      <div class="col-md-6 d-flex flex-column justify-content-center gap-2">
        <div class="d-flex align-items-center gap-2">
          <InputSwitch v-model="form.is_visible" inputId="ay_visible" />
          <label for="ay_visible" class="mb-0">ظاهر في الموقع</label>
        </div>
        <div class="d-flex align-items-center gap-2">
          <InputSwitch v-model="form.is_subscription_enabled" inputId="ay_sub" />
          <label for="ay_sub" class="mb-0">تفعيل الاشتراك</label>
        </div>
      </div>

      <div class="col-12"><hr class="my-1" /><strong>الأسعار والخطط</strong></div>
      <div class="col-md-3">
        <label class="form-label">سعر الشهر</label>
        <input type="number" class="form-control" v-model="form.monthly_price" min="0" step="0.01" />
      </div>
      <div class="col-md-3">
        <label class="form-label">سعر الترم الأول</label>
        <input type="number" class="form-control" v-model="form.term1_price" min="0" step="0.01" />
      </div>
      <div class="col-md-3">
        <label class="form-label">سعر الترم الثاني</label>
        <input type="number" class="form-control" v-model="form.term2_price" min="0" step="0.01" />
      </div>
      <div class="col-md-3">
        <label class="form-label">السعر السنوي</label>
        <input type="number" class="form-control" v-model="form.yearly_price" min="0" step="0.01" />
      </div>
      <div class="col-md-6">
        <label class="form-label">نهاية الترم الأول</label>
        <input type="date" class="form-control" v-model="form.term1_end_date" />
      </div>
      <div class="col-md-6">
        <label class="form-label">نهاية الترم الثاني</label>
        <input type="date" class="form-control" v-model="form.term2_end_date" />
      </div>
    </div>

    <template #footer>
      <button class="btn btn-secondary" @click="$emit('update:modelValue', false)">إلغاء</button>
      <button class="btn PrimaryButton" :disabled="saving" @click="save">
        <i class="pi pi-check me-1"></i>{{ saving ? 'جارٍ الحفظ...' : 'حفظ' }}
      </button>
    </template>
  </Dialog>
</template>

<script>
import axios from 'axios';
import Dialog from 'primevue/dialog';
import InputSwitch from 'primevue/inputswitch';
import Swal from 'sweetalert2';
import { PLAN_TYPES, apiError } from '@/composables/subscriptionMeta.js';

export default {
  name: 'SubscriptionSettingsModal',
  components: { Dialog, InputSwitch },
  props: {
    modelValue: { type: Boolean, default: false },
    year: { type: Object, default: null },
  },
  emits: ['update:modelValue', 'saved'],
  data() {
    return {
      form: this.blank(),
      file: null,
      preview: null,
      saving: false,
    };
  },
  watch: {
    year: {
      immediate: true,
      handler(y) {
        if (!y) return;
        this.form = {
          description: y.description ?? '',
          is_visible: y.is_visible === undefined ? true : !!y.is_visible,
          is_subscription_enabled: !!y.is_subscription_enabled,
          monthly_price: y.monthly_price ?? '', term1_price: y.term1_price ?? '',
          term2_price: y.term2_price ?? '', yearly_price: y.yearly_price ?? '',
          term1_end_date: y.term1_end_date ? String(y.term1_end_date).slice(0, 10) : '',
          term2_end_date: y.term2_end_date ? String(y.term2_end_date).slice(0, 10) : '',
        };
        this.file = null;
        this.preview = y.image_link || null;
      },
    },
  },
  methods: {
    blank() {
      return {
        description: '', is_visible: true, is_subscription_enabled: false,
        monthly_price: '', term1_price: '', term2_price: '', yearly_price: '',
        term1_end_date: '', term2_end_date: '',
      };
    },
    onImage(event) {
      const file = event.target.files[0];
      if (!file) return;
      this.file = file;
      this.preview = URL.createObjectURL(file);
    },
    async save() {
      this.saving = true;
      try {
        const fd = new FormData();
        // Laravel method spoofing so multipart (image) is parsed on the PUT route.
        fd.append('_method', 'PUT');
        const f = this.form;
        fd.append('description', f.description ?? '');
        fd.append('is_visible', f.is_visible ? 1 : 0);
        fd.append('is_subscription_enabled', f.is_subscription_enabled ? 1 : 0);
        PLAN_TYPES.forEach(t => fd.append(`${t}_price`, f[`${t}_price`] === '' ? '' : f[`${t}_price`]));
        fd.append('term1_end_date', f.term1_end_date ?? '');
        fd.append('term2_end_date', f.term2_end_date ?? '');
        if (this.file) fd.append('image', this.file);

        const { data } = await axios.post(`/academic-years/${this.year.id}`, fd);
        this.$emit('saved', data.data);
        this.$emit('update:modelValue', false);
        Swal.fire({ icon: 'success', title: 'تم الحفظ', timer: 1200, showConfirmButton: false });
      } catch (e) {
        Swal.fire({ icon: 'error', title: apiError(e), timer: 2500, showConfirmButton: false });
      } finally {
        this.saving = false;
      }
    },
  },
};
</script>
